import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";

import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";

import { FASTAPI_HOST } from "../settings/config";
import { showStyledAlert } from "./StyledAlert";

const MIC_IDLE_TIMEOUT_MS = 30_000;

function getLanguageHintFromUiLang(currentLang) {
  // Your UI uses human-readable names like "English", "Simplified Chinese"...
  // Whisper can auto-detect, but a hint can improve accuracy/latency.
  if (!currentLang) return undefined;

  const normalized = String(currentLang).trim().toLowerCase();

  if (normalized === "english" || normalized === "en") return "en";
  if (normalized === "french" || normalized === "fr" || normalized === "français") return "fr";
  if (["simplified chinese", "traditional chinese", "zh", "zh-cn", "zh-tw"].includes(normalized)) return "zh";

  return undefined; // Let backend auto-detect
}

function pickBestMimeType() {
  // Try common combos across browsers.
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4", // Safari may prefer mp4/aac
  ];

  if (!window.MediaRecorder || !window.MediaRecorder.isTypeSupported) return "";
  return candidates.find((t) => window.MediaRecorder.isTypeSupported(t)) || "";
}

function MicSvgIcon({ size = 22 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M12 15a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19 12v1a7 7 0 0 1-14 0v-1"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 19v3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 22h8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function StopSvgIcon({ size = 22 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="7" y="7" width="10" height="10" rx="2" fill="currentColor" />
    </svg>
  );
}

export default function MicTranscribeButton({
  currentLang,
  followUiLanguage = false,
  disabled,
  onTranscript,
  onError,
  onRecordingStart,
  audioOutputActive = false,
  insideInput = false,
  size = 22,
  sx: sxProp,
}) {
  const [isStarting, setIsStarting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingFailedRef = useRef(false);
  const mountedRef = useRef(true);
  const idleTimerRef = useRef(null);

  const languageHint = useMemo(() => {
    if (!followUiLanguage) return undefined;
    return getLanguageHintFromUiLang(currentLang);
  }, [currentLang, followUiLanguage]);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const stopTracks = useCallback(() => {
    clearIdleTimer();
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
    }
    streamRef.current = null;
  }, [clearIdleTimer]);

  const scheduleIdleRelease = useCallback(() => {
    clearIdleTimer();
    idleTimerRef.current = window.setTimeout(() => {
      idleTimerRef.current = null;
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === "inactive") stopTracks();
    }, MIC_IDLE_TIMEOUT_MS);
  }, [clearIdleTimer, stopTracks]);

  const getReusableStream = useCallback(async () => {
    const currentStream = streamRef.current;
    const hasUsableAudioTrack = currentStream?.active && currentStream
      .getAudioTracks().some(
      (track) => track.readyState === "live" && track.enabled && !track.muted,
    );
    if (hasUsableAudioTrack) return currentStream;

    stopTracks();
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: { ideal: 1 },
      },
    });
    streamRef.current = stream;
    return stream;
  }, [stopTracks]);

  useEffect(() => {
    // Some mobile browsers leave an idle microphone track "live" but silent
    // after switching the device audio session to assistant playback.
    if (audioOutputActive && !isRecording) stopTracks();
  }, [audioOutputActive, isRecording, stopTracks]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const recorder = recorderRef.current;
      if (recorder) {
        recorder.onstart = null;
        recorder.ondataavailable = null;
        recorder.onstop = null;
        recorder.onerror = null;
        if (recorder.state !== "inactive") {
          try {
            recorder.stop();
          } catch {
            // ignore cleanup errors
          }
        }
      }
      recorderRef.current = null;
      chunksRef.current = [];
      stopTracks();
    };
  }, [stopTracks]);

  const transcribeBlob = useCallback(
    async (blob) => {
      const userToken = Cookies.get("user_token");
      if (!userToken) {
        throw new Error("Missing user_token. Please sign in again.");
      }

      const form = new FormData();
      const ext = blob.type.includes("mp4") ? "m4a" : "webm";
      form.append("audio", blob, `recording.${ext}`);
      if (currentLang) form.append("ui_language", currentLang);
      if (languageHint) form.append("language", languageHint);

      let data;
      try {
        const response = await axios.post(`${FASTAPI_HOST}/api/stt`, form, {
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
          timeout: 60000,
        });
        data = response.data;
      } catch (error) {
        const detail = error?.response?.data?.detail;
        const message = (
          typeof detail === "string"
            ? detail
            : detail?.message
        ) || error?.message || "Speech recognition failed.";
        throw new Error(message);
      }

      const text = data?.transcript || "";
      if (onTranscript) onTranscript(text);
    },
    [currentLang, languageHint, onTranscript]
  );

  const startRecording = useCallback(async () => {
    if (disabled || isStarting || isRecording || isTranscribing) return;

    if (!navigator.mediaDevices?.getUserMedia) {
      const msg = "This browser does not support microphone access.";
      if (onError) onError(msg);
      else showStyledAlert(msg, { severity: 'error' });
      return;
    }

    if (!window.MediaRecorder) {
      const msg = "This browser does not support in-page recording (MediaRecorder). You can use the keyboard's dictation instead.";
      if (onError) onError(msg);
      else showStyledAlert(msg, { severity: 'error' });
      return;
    }

    try {
      setIsStarting(true);

      // Stop any app audio before opening the microphone so the assistant's
      // voice is not included in the next transcription.
      if (onRecordingStart) await onRecordingStart();

      // Keep the granted stream for later replies. Reopening the device and its
      // echo/noise processing on every turn can take several seconds on mobile
      // browsers and may drop the beginning of the next recording.
      clearIdleTimer();
      const stream = await getReusableStream();
      if (!mountedRef.current) {
        stopTracks();
        return;
      }

      const mimeType = pickBestMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;
      chunksRef.current = [];
      recordingFailedRef.current = false;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        setIsStarting(false);
        setIsRecording(false);
        scheduleIdleRelease();
        if (recordingFailedRef.current) {
          chunksRef.current = [];
          recorderRef.current = null;
          return;
        }
        try {
          const blob = new Blob(chunksRef.current, {
            type: recorder.mimeType || "audio/webm",
          });

          // Guardrail: avoid sending tiny/empty blobs (often decode as 0 seconds)
          if (!blob || blob.size < 2000) {
            stopTracks();
            throw new Error("Recording is too short or empty. Please record again.");
          }

          setIsTranscribing(true);
          await transcribeBlob(blob);
        } catch (e) {
          const msg = e?.message || String(e);
          if (onError) onError(msg);
          else showStyledAlert(msg, { severity: 'error' });
        } finally {
          setIsTranscribing(false);
          chunksRef.current = [];
          recorderRef.current = null;
        }
      };

      recorder.onerror = (event) => {
        recordingFailedRef.current = true;
        setIsStarting(false);
        setIsRecording(false);
        stopTracks();
        recorderRef.current = null;
        const msg = event?.error?.message || "Microphone recording failed. Please try again.";
        if (onError) onError(msg);
        else showStyledAlert(msg, { severity: 'error' });
      };

      // Emit audio data periodically so we don't end up with an empty/zero-duration blob on stop
      recorder.start(250);
      setIsStarting(false);
      setIsRecording(true);
    } catch (e) {
      setIsStarting(false);
      stopTracks();
      const msg = e?.message || String(e);
      if (onError) onError(msg);
      else showStyledAlert(msg, { severity: 'error' });
    }
  }, [
    clearIdleTimer,
    disabled,
    getReusableStream,
    isRecording,
    isStarting,
    isTranscribing,
    onError,
    onRecordingStart,
    scheduleIdleRelease,
    stopTracks,
    transcribeBlob,
  ]);

  const stopRecording = useCallback(() => {
    if (!isRecording) return;

    try {
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        // Flush buffered audio before stopping. MediaRecorder queues the data
        // event before the stop event, so an additional delay is unnecessary.
        const canRequest = typeof recorder.requestData === "function";
        try {
          if (canRequest) recorder.requestData();
        } catch {
          // ignore
        }

        recorder.stop();
      } else {
        recorderRef.current = null;
      }
    } finally {
      setIsRecording(false);
    }
  }, [isRecording]);

  const handleClick = useCallback(() => {
    if (isRecording) stopRecording();
    else startRecording();
  }, [isRecording, startRecording, stopRecording]);

  const effectiveDisabled = disabled || isStarting || isTranscribing;

  const baseSx = {
    borderRadius: 2,
    width: insideInput ? 36 : 40,
    height: insideInput ? 36 : 40,
    ml: insideInput ? 0 : 1,

    // Transparent icon button (no black background)
    backgroundColor: "transparent",
    color: isRecording ? "#B91C1C" : "#111827",

    // Subtle hover/active feedback
    "&:hover": {
      backgroundColor: isRecording ? "rgba(185, 28, 28, 0.10)" : "rgba(17, 24, 39, 0.08)",
    },

    "&.Mui-disabled": {
      backgroundColor: "transparent",
      color: "#9CA3AF",
    },
  };

  return (
    <IconButton
      color="primary"
      onClick={handleClick}
      disabled={effectiveDisabled}
      aria-label={
        isStarting
          ? "preparing microphone"
          : isRecording
            ? "stop recording"
            : "start recording"
      }
      sx={{ ...baseSx, ...(sxProp || {}) }}
    >
      {isStarting || isTranscribing ? (
        <CircularProgress size={20} sx={{ color: "currentColor" }} />
      ) : isRecording ? (
        <StopSvgIcon size={size} />
      ) : (
        <MicSvgIcon size={size} />
      )}
    </IconButton>
  );
}
